import React, { useMemo, useState } from 'react';
import {
  Button, FamilyIcon, SearchInput, SegmentedControl, ProgressBar, AvgChip, StatusDot, TwistButton, Panel,
} from '../../components/ui';
import { FAMILIES, FAMILY_ORDER, typeOfExamType } from '../../config/assessmentFamilies';
import { resultsForExamSet, statsFor } from './derive';

/** "X of Y completed" + bar, shared by interview and batch rows. */
export function Completion({ s }) {
  const pct = s.total ? Math.round((s.done / s.total) * 100) : 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 13, color: 'var(--wz-ink-2)' }}>{s.total ? `${s.done} of ${s.total} completed` : 'No candidates yet'}</span>
      <ProgressBar pct={pct} />
    </div>
  );
}

/**
 * Prototype viewInterviews(): family filter, search, New interview, and one row
 * per interview (exam set). Batch sub-rows come from `renderBatchRows` (T10,
 * see BatchUI.jsx).
 */
export default function InterviewsTab({
  data, hireBar, selectedId, onOpenInterview, onReport, onNewInterview,
  batchesByExamSet, openRows, onToggleRow, onToggleAll, renderBatchRows,
}) {
  const [famFilter, setFamFilter] = useState('all');
  const [search, setSearch] = useState('');
  const batches = true;

  const rows = useMemo(() => data.examSets.map((es) => {
    const t = typeOfExamType(es.exam_type);
    return { es, t, s: statsFor(resultsForExamSet(data.people, es.id), hireBar) };
  }), [data.examSets, data.people, hireBar]);

  const counts = useMemo(() => {
    const c = { all: rows.length };
    rows.forEach(({ t }) => { c[t.fam] = (c[t.fam] || 0) + 1; });
    return c;
  }, [rows]);

  const q = search.trim().toLowerCase();
  const shown = rows.filter(({ es, t }) => (famFilter === 'all' || t.fam === famFilter) && (!q || es.name.toLowerCase().includes(q)));
  const allOpen = batches && rows.length > 0 && rows.every(({ es }) => openRows?.has(es.id));

  const segOptions = [{ value: 'all', label: 'All', count: counts.all }, ...FAMILY_ORDER.map((f) => ({ value: f, label: FAMILIES[f].label, count: counts[f] }))]
    .filter((o) => o.count);

  return (
    <>
      <div className="wz-toolbar">
        <SegmentedControl label="Filter by family" value={famFilter} onChange={setFamFilter} options={segOptions} />
        <div className="right">
          <SearchInput label="Search interviews" placeholder="Search interviews" value={search} onChange={setSearch} />
          {batches && <Button onClick={() => onToggleAll(!allOpen)}>{allOpen ? 'Collapse all' : 'Show all batches'}</Button>}
          <Button need="exam.manage" variant="primary" icon="plus" onClick={onNewInterview}>New interview</Button>
        </div>
      </div>

      {data.examSetsError && <div className="wz-error" role="alert" style={{ marginBottom: 16 }}>{data.examSetsError}</div>}

      <Panel style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: batches ? 1140 : 1040 }}>
          <div className={`wz-ivrow th${batches ? '' : ' nobatch'}`}>
            {batches && <span />}
            <span>Interview</span>
            {batches && <span>Batches</span>}
            <span>Questions</span><span>Candidates</span><span>Completion</span><span>Avg score</span><span>Status</span><span />
          </div>
          {data.examSetsLoading ? (
            <p className="wz-sub" style={{ padding: '24px 20px' }}>Loading interviews…</p>
          ) : shown.length ? shown.map(({ es, t, s }) => {
            const open = batches && openRows?.has(es.id);
            const nb = batchesByExamSet?.[es.id]?.length ?? 0;
            return (
              <React.Fragment key={es.id}>
                <div className={`wz-ivrow${batches ? '' : ' nobatch'}${selectedId === es.id ? ' sel' : ''}`}>
                  {batches && (
                    <TwistButton expanded={!!open} label={`${open ? 'Hide' : 'Show'} batches for ${es.name}`} onClick={() => onToggleRow(es.id)} />
                  )}
                  <button type="button" className="wz-rowlink" onClick={() => onOpenInterview(es.id)}>
                    <FamilyIcon fam={t.fam} />
                    <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <span className="wz-name wz-ellipsis">{es.name}</span>
                      <span className="wz-sub">{t.name}</span>
                    </span>
                  </button>
                  {batches && <span>{nb}</span>}
                  <span>{es.question_count ?? 0}</span>
                  <span>{s.total}</span>
                  <Completion s={s} />
                  <AvgChip value={s.avg} hireBar={s.hireBar} />
                  <StatusDot live={es.is_active !== false} />
                  <div className="acts">
                    <Button small onClick={() => onReport(es.id)}>Report</Button>
                    <Button small iconOnly icon="chevR" aria-label={`Open ${es.name} details`} onClick={() => onOpenInterview(es.id)} />
                  </div>
                </div>
                {open && renderBatchRows?.(es)}
              </React.Fragment>
            );
          }) : (
            <p className="wz-sub" style={{ padding: '24px 20px' }}>{rows.length ? 'No interviews match.' : 'No interviews yet. Create one with "New interview".'}</p>
          )}
        </div>
      </Panel>
      {batches && (
        <p className="wz-sub" style={{ marginTop: 14 }}>
          Each interview runs in batches. A batch is a group of candidates invited together with its own exam window. Expand an interview to see and manage its batches.
        </p>
      )}
    </>
  );
}
